/**
 * One receipt, full-bleed, with what it is filed on underneath it.
 *
 * **This is the only place in the app that asks for the full-size file.** The
 * grid draws thumbnails and cannot do otherwise (`src/lib/receipt-library.ts`
 * is where that is a function rather than a habit); here somebody has asked to
 * read a total, so here the 2000px original is fetched, one receipt at a time.
 *
 * **Full-bleed, because a receipt is a tall thin photograph of small print.**
 * A lightbox with a card around it spends a third of a phone screen on the
 * card. The picture gets the screen and the controls sit on top of it, and the
 * one thing that must never be true is that the way out has moved — so the
 * close button is fixed to the corner and the Escape key does the same thing.
 *
 * **Zoom is the image's, not the page's.** `src/lib/pinch-zoom.ts` holds the
 * arithmetic and says why the browser's own pinch is the wrong tool: it scales
 * the toolbar and the way out along with the picture. Two fingers zoom about
 * the point between them, one finger pans once there is something to pan, a
 * double tap goes in and comes back out, and a wheel works for the half of the
 * world holding a mouse.
 *
 * **What it is filed on is underneath rather than behind a tap.** The question
 * somebody has when they open a receipt is almost always "which expense is
 * this, and is it right" — and for a receipt that is *not* on one, the answer
 * is the two buttons that put it on one: file it against an expense that
 * already exists, or make the expense from it.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { LibraryReceipt, Transaction } from '../api'
import { categoryFor } from '../lib/categories'
import { formatBytes, formatDay, formatMoney } from '../lib/format'
import {
  NO_ZOOM,
  MIN_SCALE,
  doubleTap,
  panBy,
  pinchOf,
  pinchTo,
  transformOf,
  wheelZoom,
  type Point,
  type ZoomState,
} from '../lib/pinch-zoom'
import { expenseDateFor, nameOf, ocrNote } from '../lib/receipt-library'

/** Two taps further apart than this are two taps, not a double tap. */
const DOUBLE_TAP_MS = 300
const DOUBLE_TAP_SLOP = 30

export function ReceiptViewer({
  receipt,
  currency,
  expenses,
  archived,
  busy,
  problem,
  onClose,
  onFile,
  onMakeExpense,
  onDelete,
  onOpenExpense,
}: {
  receipt: LibraryReceipt
  /** The group's own currency, for formatting what the OCR read as money. */
  currency: string
  /** The group's expenses, for filing this against one that already exists. */
  expenses: readonly Transaction[]
  archived: boolean
  /** A write is in flight; every button that starts another is disabled. */
  busy: boolean
  problem: string | null
  onClose: () => void
  onFile: (transactionId: string | null) => void
  onMakeExpense: () => void
  onDelete: () => void
  onOpenExpense: (transactionId: string) => void
}) {
  const frame = useRef<HTMLDivElement>(null)
  const picture = useRef<HTMLImageElement>(null)
  const [zoom, setZoom] = useState<ZoomState>(NO_ZOOM)

  // Every pointer currently down, by id, at its last known position. Two of
  // them is a pinch; one of them, once zoomed, is a pan.
  const pointers = useRef(new Map<number, Point>())
  const lastTap = useRef<{ at: number; point: Point } | null>(null)

  const [choice, setChoice] = useState('')

  /**
   * The frame, and the size of the picture actually drawn in it.
   *
   * The second half matters because a receipt is a tall thin photograph and is
   * therefore letterboxed: the drawn image is narrower than the frame, and a
   * clamp measured against the frame would allow half a screen of horizontal
   * travel on an axis where the picture has no overflow at all. It is read at
   * gesture time rather than remembered, so it is right after a rotation
   * without anything having to notice one.
   */
  const viewportOf = useCallback(() => {
    const box = frame.current
    const image = picture.current
    return {
      width: box?.clientWidth || 1,
      height: box?.clientHeight || 1,
      contentWidth: image?.clientWidth || undefined,
      contentHeight: image?.clientHeight || undefined,
    }
  }, [])

  // Escape is the other way out, and it has to work wherever the focus went.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // A different receipt in the same viewer starts at its own scale rather than
  // inheriting the last one's, which would open it already zoomed into a
  // corner of a picture nobody has seen yet.
  useEffect(() => {
    setZoom(NO_ZOOM)
    setChoice('')
  }, [receipt.id])

  const pointOf = (event: { clientX: number; clientY: number }): Point => {
    const box = frame.current?.getBoundingClientRect()
    return {
      x: event.clientX - (box?.left ?? 0),
      y: event.clientY - (box?.top ?? 0),
    }
  }

  function down(event: React.PointerEvent) {
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
    const point = pointOf(event)
    pointers.current.set(event.pointerId, point)

    if (pointers.current.size === 1) {
      const previous = lastTap.current
      const now = Date.now()
      if (
        previous &&
        now - previous.at < DOUBLE_TAP_MS &&
        Math.hypot(point.x - previous.point.x, point.y - previous.point.y) < DOUBLE_TAP_SLOP
      ) {
        setZoom((state) => doubleTap(state, point, viewportOf()))
        lastTap.current = null
      } else {
        lastTap.current = { at: now, point }
      }
    }
  }

  function move(event: React.PointerEvent) {
    const known = pointers.current.get(event.pointerId)
    if (!known) return

    const next = pointOf(event)
    const others = [...pointers.current.entries()].filter(([id]) => id !== event.pointerId)

    if (others.length === 1) {
      // Two fingers: the pinch before this move, and the pinch after it. Only
      // one pointer has moved, so the other's stored position is both.
      const other = others[0][1]
      const from = pinchOf(known, other)
      const to = pinchOf(next, other)
      setZoom((state) => pinchTo(state, from, to, viewportOf()))
    } else if (others.length === 0) {
      setZoom((state) => panBy(state, next.x - known.x, next.y - known.y, viewportOf()))
    }

    pointers.current.set(event.pointerId, next)
  }

  function up(event: React.PointerEvent) {
    pointers.current.delete(event.pointerId)
  }

  const transaction = receipt.transaction
  const category = categoryFor(transaction?.category ?? null)
  // The group's most recent expenses — the page the group screen already has,
  // rather than a request of its own. A receipt being filed is almost always
  // for something recent, and the one it is already on is not an option. A row
  // still being written has no id on the server to file anything against.
  const fileable = expenses.filter((row) => row.pending !== true && row.id !== transaction?.id)

  return (
    <div className="viewer" role="dialog" aria-modal="true" aria-label={nameOf(receipt)}>
      <div className="viewer__bar">
        <button type="button" className="viewer__close" onClick={onClose}>
          ← Back<span className="visually-hidden"> to the receipts</span>
        </button>
        <span className="viewer__title">{nameOf(receipt)}</span>
        <span className="viewer__actions">
          {zoom.scale > MIN_SCALE && (
            <button type="button" className="button--quiet" onClick={() => setZoom(NO_ZOOM)}>
              Fit
            </button>
          )}
          {/*
            A real link with `download`, pointed at `?download=1` — the same
            bytes the picture above came from, served as an attachment. It is
            an anchor rather than a fetch into a `blob:` URL because that would
            hold a second copy of somebody's receipt in memory for the life of
            the tab to achieve the same save dialog.
          */}
          <a className="button--quiet" href={receipt.downloadUrl} download={nameOf(receipt)}>
            Download
          </a>
          <button
            type="button"
            className="button--quiet viewer__delete"
            disabled={busy || archived}
            onClick={onDelete}
          >
            Delete
          </button>
        </span>
      </div>

      {/*
        `touch-action: none` in the stylesheet is what stops the browser
        treating these gestures as a scroll and a page pinch before they ever
        reach the handlers.
      */}
      <div
        ref={frame}
        className="viewer__frame"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onWheel={(event) => setZoom((state) => wheelZoom(state, pointOf(event), event.deltaY, viewportOf()))}
      >
        {receipt.contentType === 'application/pdf' ? (
          // A PDF is not an image and this app does not put one in an iframe:
          // the file route serves it under `default-src 'none'; sandbox`,
          // which is exactly the header that makes an embedded viewer a
          // gamble. Page 1 is the thumbnail the upload already made, and the
          // real thing is one tap away in the browser's own viewer.
          <div className="viewer__pdf">
            {receipt.thumbnailUrl && (
              <img src={receipt.thumbnailUrl} alt={`${nameOf(receipt)}, first page`} />
            )}
            <p className="muted">This one is a PDF.</p>
            <p className="actions">
              <a className="button--quiet" href={receipt.url} target="_blank" rel="noreferrer">
                Open it
              </a>
            </p>
          </div>
        ) : (
          <img
            ref={picture}
            className="viewer__image"
            src={receipt.url}
            alt={nameOf(receipt)}
            style={{ transform: transformOf(zoom) }}
            draggable={false}
          />
        )}
      </div>

      <div className="viewer__panel">
        {problem && <p className="bad note">{problem}</p>}

        {/*
          What the receipt OCR read, in words. This is a suggestion said out
          loud — never a value written anywhere on its own — and `ocrNote`
          says nothing at all for `off`, which is a free account or a PDF and
          needs no sentence.
        */}
        {ocrNote(receipt, currency) && (
          <p className={`note ${receipt.ocrStatus === 'failed' ? 'muted' : ''}`}>
            {ocrNote(receipt, currency)}
          </p>
        )}

        {transaction && transaction.deletedAt === null ? (
          <>
            <p className="viewer__filed">
              {/* Takes you to where that expense lives, which is the group's
                  own screen. */}
              <button
                type="button"
                className="button--quiet viewer__expense"
                onClick={() => onOpenExpense(transaction.id)}
              >
                <strong>{transaction.description}</strong>
                {category && (
                  <span className="pill">
                    <span aria-hidden="true">{category.glyph}</span>
                    {category.label}
                  </span>
                )}
              </button>
              <span className="viewer__amount">
                {formatMoney(transaction.amountCents, transaction.currency)} ·{' '}
                {formatDay(transaction.occurredOn)}
              </span>
            </p>
            <p className="actions actions--tight">
              <button
                type="button"
                className="button--quiet"
                disabled={busy || archived}
                onClick={() => onFile(null)}
              >
                Take it off this expense
              </button>
            </p>
          </>
        ) : (
          <>
            <p className="note">
              {transaction === null
                ? 'Not filed on anything yet.'
                : 'The expense this was on has been removed, so it needs filing again.'}{' '}
              <span className="muted">
                Photographed {formatDay(expenseDateFor(receipt))}.
              </span>
            </p>
            <p className="actions actions--tight">
              <button type="button" disabled={busy || archived} onClick={onMakeExpense}>
                Make an expense from this
              </button>
            </p>
            <p className="actions actions--tight">
              <label className="visually-hidden" htmlFor="viewer-file-on">
                File it on an expense that already exists
              </label>
              <select
                id="viewer-file-on"
                value={choice}
                disabled={busy || archived || fileable.length === 0}
                onChange={(event) => setChoice(event.target.value)}
              >
                <option value="">
                  {fileable.length === 0
                    ? 'No expenses to file it on'
                    : 'Or file it on an expense…'}
                </option>
                {fileable.map((row) => (
                  <option key={row.id} value={row.id}>
                    {formatDay(row.occurredOn)} · {row.description} ·{' '}
                    {formatMoney(row.amountCents, row.currency)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="button--quiet"
                disabled={busy || archived || choice === ''}
                onClick={() => onFile(choice)}
              >
                File it
              </button>
            </p>
          </>
        )}

        <p className="muted note">
          {formatBytes(receipt.sizeBytes)} · added {receipt.createdAt.slice(0, 10)}
        </p>
      </div>
    </div>
  )
}
